import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Helmet } from '@/components/Helmet';
import { ShieldAlert } from 'lucide-react';
import Dashboard from './Dashboard';

export default function AdminImpersonationDashboard() {
  const { user, loading } = useAuth();
  const { setImpersonation, clearImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const { userId } = useParams();

  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  const { data: impersonationContext, isLoading: contextLoading, error: contextError } = useQuery({
    queryKey: ['admin-impersonation-context', userId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-impersonation-context', {
        body: { customerId: userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data || null;
    },
    enabled: !!userId && isAdmin === true,
  });

  const targetLabel = useMemo(() => {
    if (impersonationContext?.customer?.email) return impersonationContext.customer.email;
    if (impersonationContext?.customer?.fullName) return impersonationContext.customer.fullName;
    return userId || 'Unknown user';
  }, [impersonationContext, userId]);

  useEffect(() => {
    if (!userId) return;
    if (loading) return;
    if (isAdmin === false) {
      navigate('/portal', { replace: true });
    }
  }, [isAdmin, loading, navigate, userId]);

  useEffect(() => {
    if (!contextError) return;
    clearImpersonation();
    const timeout = window.setTimeout(() => {
      navigate('/portal/admin', { replace: true });
    }, 1500);
    return () => window.clearTimeout(timeout);
  }, [clearImpersonation, contextError, navigate]);

  useEffect(() => {
    if (!impersonationContext?.customer?.id) return;
    setImpersonation({
      activeCustomerId: impersonationContext.customer.id,
      activeCustomerEmail: impersonationContext.customer.email ?? null,
      activeCustomerName: impersonationContext.customer.fullName ?? null,
      notifications: impersonationContext.notifications ?? [],
    });
  }, [impersonationContext, setImpersonation]);

  // Impersonation state is managed via sessionStorage and persists across navigation.
  // It is cleared explicitly by the admin (e.g., "Stop Impersonating" action) or
  // when navigating back to the admin panel, not on component unmount.

  if (!userId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin Impersonation | ShadowScan Portal</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="sticky top-0 z-50 border-b border-[hsl(var(--ss-warning)/0.4)] bg-[hsl(var(--ss-warning)/0.12)] backdrop-blur">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-[hsl(var(--ss-warning))]" />
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-[hsl(var(--ss-warning))]">Viewing customer: {targetLabel} — Admin mode</span>
              <span className="text-xs text-[hsl(var(--ss-warning))]/70">Actions are logged as admin-initiated.</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="warning">Impersonation</Badge>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clearImpersonation();
                navigate('/portal/admin');
              }}
            >
              Exit customer view
            </Button>
          </div>
        </div>
      </div>
      {contextError && (
        <div className="container mx-auto max-w-6xl px-4 pt-6 text-sm text-[hsl(var(--ss-warning))]">
          Unable to load the selected customer. Returning to Admin Console.
        </div>
      )}
      {contextLoading ? (
        <div className="container mx-auto max-w-6xl px-4 py-8 text-sm text-muted-foreground">
          Loading customer dashboard...
        </div>
      ) : impersonationContext?.customer?.id ? (
        <Dashboard
          allowAdminView
          impersonationExitTo="/portal/admin"
          impersonationContext={impersonationContext}
        />
      ) : (
        <div className="container mx-auto max-w-6xl px-4 py-8 text-sm text-muted-foreground">
          Customer context unavailable.
        </div>
      )}
    </div>
  );
}
